// Shared simulator knobs (avgResponseHrs, overlap, errorRate, etc.).
//
// Both the sandbox knobs and the story-mode demo read/write through this
// module so the user's "this is *my* number" doesn't get lost when they jump
// between views. Persisted to localStorage so the next visit keeps the dial.

import { HOURLY_RATE, PROCS_PER_YEAR } from "./processes";

const STORAGE_KEY = "mp-params";

export interface SimParams {
  avgResponseHrs: number;
  overlap: number;
  errorRate: number;
  oooRate: number;
  dropRate: number;
  automationPct: number;
  aiCoverage: number;
  hourlyRate: number;
  procsPerYear: number;
}

export type ParamId = keyof SimParams;

export const DEFAULT_PARAMS: SimParams = {
  avgResponseHrs: 24,
  overlap: 0.7,
  errorRate: 0.08,
  oooRate: 0.06,
  dropRate: 0.01,
  automationPct: 0.7,
  aiCoverage: 0.9,
  hourlyRate: HOURLY_RATE,
  procsPerYear: PROCS_PER_YEAR,
};

const state: SimParams = { ...DEFAULT_PARAMS };
const listeners = new Set<(p: SimParams) => void>();

function persist(): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function notify(): void {
  listeners.forEach(cb => {
    try { cb(getParams()); } catch { /* ignore */ }
  });
}

function isParamId(k: string): k is ParamId {
  return Object.prototype.hasOwnProperty.call(DEFAULT_PARAMS, k);
}

export function loadParams(): void {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (raw && typeof raw === "object") {
      const obj = raw as Record<string, unknown>;
      for (const k of Object.keys(DEFAULT_PARAMS) as ParamId[]) {
        const v = obj[k];
        if (typeof v === "number" && isFinite(v)) state[k] = v;
      }
    }
  } catch { /* ignore */ }
}

export function getParams(): SimParams {
  return { ...state };
}

// Direct mutation (used by buildKnobs which binds to live params object)
export function getParamsLive(): SimParams {
  return state;
}

export function setParam(id: string, value: number): void {
  if (!isParamId(id)) return;
  state[id] = value;
  persist();
  notify();
}

// Replace several params at once — used when loading a process changes
// avgResponseHrs based on that process's baseline.
export function setParams(patch: Partial<Record<ParamId, number>> | null | undefined): void {
  let changed = false;
  for (const [k, v] of Object.entries(patch || {})) {
    if (isParamId(k) && typeof v === "number") {
      state[k] = v;
      changed = true;
    }
  }
  if (changed) {
    persist();
    notify();
  }
}

export function onParamsChange(cb: (p: SimParams) => void): () => boolean {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function resetParams(): void {
  Object.assign(state, DEFAULT_PARAMS);
  persist();
  notify();
}
